import { useCallback, useEffect, useRef, useState } from 'react'
import type { ActionChoiceDto, ActionPlanItem, ActionToAddDto } from '@/infra/api/generated/model'
import {
  findPreselectedActions,
  removeAction,
  toggleAction,
} from '@/src/utils/actionSelectionUtils'

type UseActionSelectionParams = {
  isOpen: boolean
  choices: ActionChoiceDto[] | undefined
  selectedActions: ActionPlanItem[]
}

export const useActionSelection = ({
  isOpen,
  choices,
  selectedActions,
}: UseActionSelectionParams) => {
  const [selection, setSelection] = useState<ActionToAddDto[]>([])
  const isInitialized = useRef(false)

  useEffect(() => {
    if (!isOpen) {
      isInitialized.current = false
      return
    }
    if (choices && !isInitialized.current) {
      isInitialized.current = true
      setSelection(findPreselectedActions(selectedActions, choices))
    }
  }, [isOpen, choices, selectedActions])

  const toggle = useCallback(
    (action: ActionToAddDto) => setSelection((prev) => toggleAction(prev, action)),
    []
  )
  const add = useCallback((action: ActionToAddDto) => setSelection((prev) => [...prev, action]), [])
  const remove = useCallback(
    (actionId: string) => setSelection((prev) => removeAction(prev, actionId)),
    []
  )

  return { selection, toggle, add, remove }
}
